import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { API } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { History, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const PracticeHistory = () => {
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAttempts();
  }, []);

  const fetchAttempts = async () => {
    try {
      const response = await axios.get(`${API}/practice-history`);
      setAttempts(response.data || []);
    } catch (error) {
      toast.error('Failed to load practice attempts');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="practice-history-loading">
        <div className="text-lg text-slate-600">Loading practice history...</div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="practice-history-page">
      <div className="mb-8">
        <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-2">Practice History</h1>
        <p className="text-lg text-slate-600">Revisit past practice tests and analysis</p>
      </div>

      <Card className="border-0 shadow-xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="w-5 h-5 text-violet-600" />
            Recent Attempts
          </CardTitle>
          <CardDescription>Your latest practice test submissions</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {attempts.length === 0 ? (
            <div className="text-sm text-slate-500" data-testid="practice-history-empty">
              No practice attempts yet. Create a practice test to get started.
            </div>
          ) : (
            attempts.map((attempt) => (
              <div key={attempt.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-4 rounded-xl border border-slate-200">
                <div>
                  <p className="font-semibold text-slate-900">{attempt.exam} · {attempt.mode === 'full' ? 'Full Test' : 'Part Test'}</p>
                  <p className="text-sm text-slate-600" data-testid={`practice-history-score-${attempt.id}`}>
                    Score: {attempt.score}{attempt.max_score ? ` / ${attempt.max_score}` : ''} · {attempt.percentage}%
                  </p>
                </div>
                <Button asChild variant="outline" className="rounded-full" data-testid={`practice-history-view-${attempt.id}`}>
                  <Link to={`/practice/results/${attempt.id}`}>
                    View Analysis <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </Button>
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PracticeHistory;